import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, MessageCircle, Search, FileCheck, Clock, Shield, CheckCircle } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const HowItWorks = () => {
  const steps = [
    {
      number: "01",
      icon: MessageCircle,
      title: "Answer a Few Simple Questions",
      description: "Tell us about yourself - your age, coverage needs, health status, and preferences. Our smart form adapts to your answers.",
      details: [
        "Basic information (age, state, coverage amount)",
        "Health and lifestyle questions",
        "Policy preferences and priorities",
        "Takes just 2-3 minutes to complete"
      ],
      color: "text-blue-600"
    },
    {
      number: "02", 
      icon: Search,
      title: "Get Matched to a Top Carrier",
      description: "Our AI analyzes your profile against 15+ A-rated carriers to find the perfect match based on your unique situation.",
      details: [
        "AI compares rates across top carriers",
        "Matches based on your health profile",
        "Considers your budget and preferences", 
        "Only shows relevant, qualified options"
      ],
      color: "text-green-600"
    },
    {
      number: "03",
      icon: FileCheck, 
      title: "Apply with No Phone Calls",
      description: "Complete your application online at your own pace. Many policies require no medical exam and can be approved instantly.",
      details: [
        "Secure online application process",
        "No pushy sales calls or pressure",
        "Medical exam optional for many policies",
        "Get approved in as little as 24 hours"
      ],
      color: "text-purple-600"
    }
  ];

  const benefits = [
    {
      icon: Clock,
      title: "Save Time",
      description: "Complete the entire process in minutes, not weeks"
    },
    {
      icon: Shield,
      title: "Stay Protected",
      description: "Your information is secure and never sold to third parties"
    },
    {
      icon: CheckCircle,
      title: "No Pressure",
      description: "Compare options without aggressive sales tactics"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-primary-light/30 to-background">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
              How It Works
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Getting life insurance has never been this simple. Here's how we make it happen in just 3 easy steps.
            </p>
            <Link to="/">
              <Button size="lg" variant="hero">
                Start Your Quote Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Three Simple Steps to Coverage
              </h2>
              <p className="text-xl text-muted-foreground">
                Our streamlined process gets you covered quickly and efficiently.
              </p>
            </div>
            
            <div className="space-y-16">
              {steps.map((step, index) => (
                <div key={index} className="relative">
                  {/* Connection Line */}
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute left-1/2 top-full w-px h-16 bg-border transform -translate-x-1/2 mt-8"></div>
                  )}
                  
                  <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                    <CardContent className="p-0">
                      <div className={`grid lg:grid-cols-2 gap-8 ${index % 2 === 1 ? 'lg:grid-cols-2' : ''}`}>
                        {/* Content */}
                        <div className={`p-8 lg:p-12 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                          <div className="flex items-center mb-6">
                            <div className="text-4xl font-bold text-primary mr-4">
                              {step.number}
                            </div>
                            <step.icon className="h-8 w-8 text-primary" />
                          </div>
                          
                          <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                            {step.title}
                          </h3>
                          
                          <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                            {step.description}
                          </p>
                          
                          <ul className="space-y-3">
                            {step.details.map((detail, detailIndex) => (
                              <li key={detailIndex} className="flex items-start">
                                <CheckCircle className="h-5 w-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                                <span className="text-muted-foreground">{detail}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        {/* Visual */}
                        <div className={`bg-primary/5 p-8 lg:p-12 flex items-center justify-center ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                          <div className="w-48 h-48 bg-primary/10 rounded-3xl flex items-center justify-center">
                            <step.icon className="h-24 w-24 text-primary" />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-primary-light/30">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Why Choose Our Process?
              </h2>
              <p className="text-xl text-muted-foreground">
                Traditional insurance shopping is outdated. Here's why our approach works better.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <Card key={index} className="text-center p-8 hover:shadow-lg transition-shadow">
                  <CardContent className="p-0">
                    <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <benefit.icon className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-3">
                      {benefit.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Traditional vs. Safurra
              </h2>
              <p className="text-xl text-muted-foreground">
                See how our modern approach compares to traditional insurance shopping.
              </p>
            </div>
            
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <div className="grid md:grid-cols-2">
                  {/* Traditional */}
                  <div className="p-8 bg-red-50/50">
                    <h3 className="text-xl font-bold text-foreground mb-6 text-center">
                      Traditional Process
                    </h3>
                    <div className="space-y-4 text-sm">
                      <div className="flex items-start">
                        <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-muted-foreground">Call multiple agents individually</span>
                      </div>
                      <div className="flex items-start">
                        <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-muted-foreground">Repeat your information multiple times</span>
                      </div>
                      <div className="flex items-start">
                        <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-muted-foreground">Deal with high-pressure sales tactics</span>
                      </div>
                      <div className="flex items-start">
                        <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-muted-foreground">Wait weeks for quotes and approval</span>
                      </div>
                      <div className="flex items-start">
                        <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-muted-foreground">Confusing policy comparisons</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Safurra */}
                  <div className="p-8 bg-primary/5">
                    <h3 className="text-xl font-bold text-foreground mb-6 text-center">
                      The Safurra Way
                    </h3>
                    <div className="space-y-4 text-sm">
                      <div className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 mr-3 flex-shrink-0" />
                        <span className="text-muted-foreground">One simple online form</span>
                      </div>
                      <div className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 mr-3 flex-shrink-0" />
                        <span className="text-muted-foreground">AI matches you to the best carriers</span>
                      </div>
                      <div className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 mr-3 flex-shrink-0" />
                        <span className="text-muted-foreground">No sales calls or pressure</span>
                      </div>
                      <div className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 mr-3 flex-shrink-0" />
                        <span className="text-muted-foreground">Instant quotes and fast approval</span>
                      </div>
                      <div className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 mr-3 flex-shrink-0" />
                        <span className="text-muted-foreground">Clear, personalized recommendations</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-light/30">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              Ready to Experience the Difference?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Join thousands who've found better coverage through our simple, transparent process.
            </p>
            <Link to="/">
              <Button size="lg" variant="hero">
                Start Your Quote Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HowItWorks;